import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import WorkflowProgress from '../../components/ui/WorkflowProgress';
import SyncStatusNotifications from '../../components/ui/SyncStatusNotifications';
import ProgressCircle from '../../components/ProgressCircle';
import CurrentActivity from './components/CurrentActivity';
import ActivityLog from './components/ActivityLog';
import MetricsCards from './components/MetricsCards';
import SyncControls from './components/SyncControls';
import ErrorDetails from './components/ErrorDetails';
import MilestoneAnimation from './components/MilestoneAnimation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const SyncProgressRealTimeTracking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Sync state
  const [syncState, setSyncState] = useState({
    isRunning: true,
    isPaused: false,
    progress: 0,
    status: 'searching'
  });

  // Current activity state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState('5m 30s');

  // Metrics state
  const [metrics, setMetrics] = useState({
    total: 150,
    processed: 0,
    successful: 0,
    failed: 0,
    duplicates: 0
  });

  // Activity log state
  const [activities, setActivities] = useState([]);
  const [isLogExpanded, setIsLogExpanded] = useState(false);

  // Error handling state
  const [errors, setErrors] = useState([]);

  // Milestone animation state
  const [currentMilestone, setCurrentMilestone] = useState(null);

  // Navigation prevention state
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Mock data for demonstration
  const mockTracks = [
    {
      id: 1,
      title: "Bohemian Rhapsody",
      artist: "Queen",
      album: "A Night at the Opera",
      duration: "5:55",
      albumArt: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=300&h=300&fit=crop"
    },
    {
      id: 2,
      title: "Stairway to Heaven",
      artist: "Led Zeppelin",
      album: "Led Zeppelin IV",
      duration: "8:02",
      albumArt: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=300&h=300&fit=crop"
    },
    {
      id: 3,
      title: "Hotel California",
      artist: "Eagles",
      album: "Hotel California",
      duration: "6:30",
      albumArt: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=300&h=300&fit=crop"
    }
  ];

  // Simulate sync progress
  useEffect(() => {
    if (!syncState.isRunning || syncState.isPaused) return;

    const interval = setInterval(() => {
      setSyncState(prev => {
        const newProgress = Math.min(prev.progress + Math.random() * 3, 100);
        return { ...prev, progress: newProgress };
      });

      setMetrics(prev => {
        const newProcessed = Math.min(prev.processed + 1, prev.total);
        const newSuccessful = prev.successful + (Math.random() > 0.1 ? 1 : 0);
        const newFailed = prev.failed + (Math.random() > 0.9 ? 1 : 0);
        const newDuplicates = prev.duplicates + (Math.random() > 0.95 ? 1 : 0);

        return {
          ...prev,
          processed: newProcessed,
          successful: Math.min(newSuccessful, newProcessed),
          failed: Math.min(newFailed, newProcessed - newSuccessful),
          duplicates: newDuplicates
        };
      });

      // Update current track
      if (Math.random() > 0.7) {
        const randomTrack = mockTracks[Math.floor(Math.random() * mockTracks.length)];
        setCurrentTrack(randomTrack);
        
        const statuses = ['searching', 'matching', 'adding', 'completed'];
        const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
        setSyncState(prev => ({ ...prev, status: randomStatus }));
      }

      // Add activity log entries
      if (Math.random() > 0.6) {
        const activities = [
          {
            type: 'success',
            message: `Successfully matched "${mockTracks[0].title}" by ${mockTracks[0].artist}`,
            timestamp: new Date(),
            details: 'Match confidence: 98%'
          },
          {
            type: 'failed',
            message: `Could not find match for "${mockTracks[1].title}"`,
            timestamp: new Date(),
            details: 'No suitable matches found in target platform'
          },
          {
            type: 'duplicate',
            message: `Skipped duplicate: "${mockTracks[2].title}"`,
            timestamp: new Date(),
            details: 'Track already exists in target playlist'
          }
        ];
        
        const randomActivity = activities[Math.floor(Math.random() * activities.length)];
        setActivities(prev => [...prev, randomActivity].slice(-50));
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [syncState.isRunning, syncState.isPaused]);

  // Check for milestones
  useEffect(() => {
    const progress = Math.floor(syncState.progress);
    const milestones = [25, 50, 75, 100];
    
    milestones.forEach(milestone => {
      if (progress >= milestone && !currentMilestone && progress > 0) {
        setCurrentMilestone({
          percentage: milestone,
          stats: {
            processed: metrics.processed,
            successful: metrics.successful
          }
        });
      }
    });
  }, [syncState.progress, metrics, currentMilestone]);

  // Handle sync completion
  useEffect(() => {
    if (syncState.progress >= 100 && syncState.isRunning) {
      setSyncState(prev => ({ ...prev, isRunning: false, status: 'completed' }));
      
      // Auto-navigate to results after completion animation
      setTimeout(() => {
        navigate('/sync-results-management', {
          state: {
            syncResults: {
              total: metrics.total,
              successful: metrics.successful,
              failed: metrics.failed,
              duplicates: metrics.duplicates,
              completedAt: new Date()
            }
          }
        });
      }, 3000);
    }
  }, [syncState.progress, syncState.isRunning, metrics, navigate]);

  // Navigation prevention
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (syncState.isRunning && syncState.progress < 100) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [syncState.isRunning, syncState.progress]);

  const handlePause = () => {
    setSyncState(prev => ({ ...prev, isPaused: true }));
  };

  const handleResume = () => {
    setSyncState(prev => ({ ...prev, isPaused: false }));
  };

  const handleCancel = () => {
    setSyncState(prev => ({ 
      ...prev, 
      isRunning: false, 
      isPaused: false, 
      status: 'cancelled' 
    }));
    
    setTimeout(() => {
      navigate('/playlist-selection-sync-setup');
    }, 1000);
  };

  const handleRetryError = (errorId) => {
    setErrors(prev => prev.filter(error => error.id !== errorId));
    // Simulate retry logic
  };

  const handleManualOverride = (errorId, overrideData) => {
    setErrors(prev => prev.filter(error => error.id !== errorId));
    setMetrics(prev => ({ 
      ...prev, 
      successful: prev.successful + 1,
      failed: Math.max(prev.failed - 1, 0)
    }));
  };

  const handleMilestoneComplete = () => {
    setCurrentMilestone(null);
  };

  const handleNavigationAttempt = (path) => {
    if (syncState.isRunning && syncState.progress < 100) {
      setShowNavigationWarning(true);
      setPendingNavigation(path);
    } else {
      navigate(path);
    }
  };

  const confirmNavigation = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const cancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* <Header /> */}
      <WorkflowProgress />
      {/* <SyncStatusNotifications /> */}

      <main className="pt-32 lg:pt-36 pb-8">
        <div className="px-4 lg:px-6 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="RefreshCw" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Sync Progress
                </h1>
                <p className="text-text-secondary">
                  Real-time tracking of your playlist synchronization
                </p>
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1 flex justify-center">
              <ProgressCircle progress={syncState.progress} />
            </div>
            
            <div className="lg:col-span-2 space-y-6">
              <CurrentActivity
                currentTrack={currentTrack}
                estimatedTimeRemaining={estimatedTimeRemaining}
                status={syncState.status}
              />
              
              <SyncControls
                isRunning={syncState.isRunning}
                isPaused={syncState.isPaused}
                onPause={handlePause}
                onResume={handleResume}
                onCancel={handleCancel}
              />
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="mb-8">
            <MetricsCards metrics={metrics} />
          </div>

          {/* Activity Log and Error Details */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <ActivityLog
              activities={activities}
              isExpanded={isLogExpanded}
              onToggleExpanded={() => setIsLogExpanded(!isLogExpanded)}
            />
            
            {errors.length > 0 && (
              <ErrorDetails
                errors={errors}
                onRetry={handleRetryError}
                onManualOverride={handleManualOverride}
              />
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-surface border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-text-primary mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleNavigationAttempt('/dashboard-sync-overview')}
                iconName="LayoutDashboard"
                iconSize={16}
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleNavigationAttempt('/sync-results-management')}
                iconName="History"
                iconSize={16}
              >
                View History
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleNavigationAttempt('/authentication-account-connection')}
                iconName="Users"
                iconSize={16}
              >
                Manage Accounts
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Milestone Animation */}
      <MilestoneAnimation
        milestone={currentMilestone}
        onComplete={handleMilestoneComplete}
      />

      {/* Navigation Warning Dialog */}
      {showNavigationWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1050 p-4">
          <div className="bg-surface border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-warning/10 rounded-lg">
                <Icon name="AlertTriangle" size={24} className="text-warning" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-text-primary">Sync in Progress</h3>
                <p className="text-sm text-text-secondary">Navigation will interrupt the sync</p>
              </div>
            </div>

            <p className="text-sm text-text-secondary mb-6">
              Your playlist sync is currently running. Leaving this page will cancel the sync operation and you'll lose your progress.
            </p>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelNavigation}
              >
                Stay Here
              </Button>
              <Button
                variant="warning"
                size="sm"
                onClick={confirmNavigation}
                iconName="ExternalLink"
                iconSize={16}
              >
                Leave Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncProgressRealTimeTracking;